import {TextField} from "@mui/material";
import {Fragment} from "react";
import {MutationSaveLocationArgs} from "../../../api";
import {Controller, FieldError} from "./controller";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";
import {FormattedFormRow} from "@dvrt/graphql-widgets";
import {useStructureConfig} from "@/context/StructureConfigProvider";

type ValueFormatter = {
    in?: (val: any) => any;
    out?: (val: string) => any;
};

type FieldProps = {
    type: string;
    name: keyof MutationSaveLocationArgs;
    label: MessageDescriptor;
    required?: boolean;
    controller: Controller;
    valueFormatter?: ValueFormatter;
    disabled?: boolean;
};

const FormattedError = (props: {error: FieldError}) => {
    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
    return props.error ? <FormattedMessage id={props.error.type} values={props.error.context} /> : null;
};

const FieldRow = ({type, name, label, required, controller, valueFormatter, disabled}: FieldProps) => (
    <FormattedFormRow label={label} required={required}>
        <TextField
            variant="standard"
            fullWidth
            margin="dense"
            type={type}
            value={valueFormatter?.in ? valueFormatter.in(controller.data[name]) : controller.data[name]}
            error={controller.hasError(name)}
            helperText={<FormattedError error={controller.errors[name]} />}
            onChange={(ev) =>
                controller.updateData(name, valueFormatter?.out ? valueFormatter.out(ev.target.value) : ev.target.value)
            }
            disabled={disabled}
        />
    </FormattedFormRow>
);

type Props = {
    controller: Controller;
    canEdit: boolean;
};

const numberValueFormatter: ValueFormatter = {
    out: (val: string) => parseInt(val),
};

const nullStringValueFormatter: ValueFormatter = {
    in: (val: any) => (val == null ? "" : val),
    out: (val: string) => (val === "" ? null : val),
};

const Form = ({controller, canEdit}: Props) => {
    const {isEnabledFeature} = useStructureConfig();

    return (
        <Fragment>
            <FieldRow
                controller={controller}
                type="text"
                name="name"
                label={defineMessage({id: "name", defaultMessage: "Name"})}
                required
                disabled={!canEdit}
            />
            <FieldRow
                controller={controller}
                type="text"
                name="description"
                label={defineMessage({id: "description", defaultMessage: "Description"})}
                required
                disabled={!canEdit}
            />
            <FieldRow
                controller={controller}
                type="text"
                name="pilotNumber"
                label={defineMessage({id: "pilotNumber", defaultMessage: "Pilot number"})}
                required
                disabled={!canEdit}
            />
            <FieldRow
                controller={controller}
                type="number"
                name="callLimit"
                label={defineMessage({id: "callLimit", defaultMessage: "Parallel call limit"})}
                valueFormatter={numberValueFormatter}
                disabled={!canEdit}
            />
            <FieldRow
                controller={controller}
                type="number"
                name="bandwidthLimit"
                label={defineMessage({id: "bandwidthLimit", defaultMessage: "Bandwidth limit"})}
                valueFormatter={numberValueFormatter}
                disabled={!canEdit}
            />
            <FieldRow
                controller={controller}
                type="text"
                name="ipAddress"
                label={defineMessage({id: "ipAddress", defaultMessage: "IP address"})}
                valueFormatter={nullStringValueFormatter}
                disabled={!canEdit}
            />
            {isEnabledFeature("emergencyPrefix") && (
                <FieldRow
                    controller={controller}
                    type="text"
                    name="emergencyPrefix"
                    label={defineMessage({id: "emergencyPrefix", defaultMessage: "Emergency prefix"})}
                    valueFormatter={nullStringValueFormatter}
                    disabled={!canEdit}
                />
            )}
        </Fragment>
    );
};

export default Form;
